import React, { useState, useEffect } from 'react';
import { useSession } from '../../Utils/SessionContext';
import useSkillsAutocomplete from '../../Utils/useSkillsAutocomplete';
import ComboBoxSelect from './ComboBoxSelect';

const CBS_HardSkills = ({
  criteria,
  onCriteriaChange,
  label = "Hard Skills",
  placeholder = "Type to search or add custom hard skills...",
  showWarning = false
}) => {
  const { user } = useSession();

  // Skills autocomplete hook
  const { skillsOptions, isLoadingSkills, searchSkills, clearSkills } = useSkillsAutocomplete();

  // Extract hard skills from criteria
  const getInitialHardSkills = () => {
    if (criteria?.hardSkills?.items) {
      return criteria.hardSkills.items.map((item) => ({
        value: item.skill.toLowerCase().replace(/\s+/g, '-').replace(/[()]/g, ''),
        label: item.importance === 'Required' ? `${item.skill} ⭐` : item.skill,
        color: '#3b82f6', // Keep all skills blue
        importance: item.importance
      }));
    }
    return [];
  };

  // State for selected hard skills
  const [selectedSkills, setSelectedSkills] = useState(getInitialHardSkills());

  // Update selected skills when criteria changes
  useEffect(() => {
    setSelectedSkills(getInitialHardSkills());
  }, [criteria]);

  // Update hard skills criteria JSON
  const updateHardSkillsCriteria = (newSelectedSkills) => {
    if (!criteria) return;

    // Create updated criteria with new hard skills
    const updatedCriteria = {
      ...criteria,
      hardSkills: {
        ...criteria.hardSkills,
        items: newSelectedSkills.map(skill => {
          // Remove star from label to get clean skill name
          const cleanSkillName = skill.label.replace(' ⭐', '');
          
          // Check if this skill already exists in our current criteria
          const existingSkill = criteria.hardSkills?.items?.find(
            item => item.skill === cleanSkillName
          );
          
          if (existingSkill) {
            // Keep existing skill with all its properties
            return existingSkill;
          } else {
            // Create new skill entry with hardSkills specific structure
            return {
              skill: cleanSkillName,
              context: [],
              importance: "Required"
            };
          }
        })
      }
    };

    // Call parent callback with only the updated criteria
    onCriteriaChange(updatedCriteria);
  };

  // Handle skill selection change
  const handleSkillChange = (newValue, actionMeta) => {
    const newSelectedSkills = newValue || [];
    setSelectedSkills(newSelectedSkills);
    updateHardSkillsCriteria(newSelectedSkills);
  };

  // Handle search input change
  const handleSearchInputChange = (inputValue, { action }) => {
    if (action === 'input-change') {
      if (inputValue && inputValue.length >= 2) {
        searchSkills(inputValue);
      } else {
        clearSkills();
      }
    }
  };

  // Handle creating new skill option
  const handleCreateOption = (inputValue) => {
    const newOption = {
      value: inputValue.toLowerCase().replace(/\s+/g, '-'),
      label: inputValue,
      color: '#3b82f6',
      __isNew__: true
    };
    const updatedSkills = [...selectedSkills, newOption];
    setSelectedSkills(updatedSkills);
    updateHardSkillsCriteria(updatedSkills);
  };

  return (
    <div className="mb-6">
      <ComboBoxSelect
        value={selectedSkills}
        onChange={handleSkillChange}
        options={skillsOptions}
        placeholder={placeholder}
        label={label}
        onInputChange={handleSearchInputChange}
        isLoading={isLoadingSkills}
        onCreateOption={handleCreateOption}
      />
      {showWarning && (
        <div className="flex items-center justify-end gap-1 -mt-2 mb-2 text-xs text-red-600">
          <span>⚠️</span>
          <span>Over 10 items may reduce evaluation accuracy.</span>
        </div>
      )}
    </div>
  );
};

export default CBS_HardSkills;
